export default function registerAdeptConnectionWizard(Alpine) {
  Alpine.data("adeptConnectionWizard", ({ courses_url, settings_url, existing }) => ({
    mode: existing.mode || "",
    courseId: existing.course_id || "",
    courseSettingId: existing.course_setting_id || "",
    courses: [],
    settings: [],
    loadingCourses: false,
    loadingSettings: false,
    coursesError: "",
    settingsError: "",
    submitting: false,
    coursesUrl: courses_url,
    settingsUrl: settings_url,
    courseQuery: existing.course_title || "",
    courseListOpen: false,
    activeCourseIndex: -1,

    init() {
      this.loadCourses();
      if (this.mode === "course_access" && this.courseId) {
        this.loadSettings();
      }
    },

    get canSubmit() {
      if (this.submitting) return false;
      if (!this.mode || !this.courseId) return false;
      if (this.mode === "course_access" && !this.courseSettingId) return false;
      return true;
    },

    // Display-only labels persisted on connect so the product card can show what
    // fulfillment does without a live Adept lookup.
    get selectedCourseTitle() {
      const course = this.courses.find((c) => String(c.id) === String(this.courseId));
      return course ? course.title : "";
    },

    get selectedSettingLabel() {
      const setting = this.settings.find((s) => String(s.id) === String(this.courseSettingId));
      return setting ? setting.name : "";
    },

    get filteredCourses() {
      const q = this.courseQuery.trim().toLowerCase();
      if (!q || q === this.selectedCourseTitle.toLowerCase()) return this.courses;
      return this.courses.filter((c) =>
        String(c.title || "")
          .toLowerCase()
          .includes(q)
      );
    },

    openCourseList() {
      this.courseListOpen = true;
      this.activeCourseIndex = this.filteredCourses.findIndex((c) => String(c.id) === String(this.courseId));
    },

    closeCourseList() {
      this.courseListOpen = false;
      this.activeCourseIndex = -1;
      // Typed text that wasn't picked snaps back to the current selection.
      this.courseQuery = this.selectedCourseTitle;
    },

    onCourseInput() {
      this.courseListOpen = true;
      this.activeCourseIndex = this.filteredCourses.length ? 0 : -1;
    },

    moveCourseHighlight(step) {
      if (!this.courseListOpen) {
        this.openCourseList();
        return;
      }
      const count = this.filteredCourses.length;
      if (!count) return;
      this.activeCourseIndex = (this.activeCourseIndex + step + count) % count;
      this.$nextTick(() => {
        document.getElementById(`adept-course-option-${this.activeCourseIndex}`)?.scrollIntoView({ block: "nearest" });
      });
    },

    chooseActiveCourse() {
      const course = this.filteredCourses[this.activeCourseIndex];
      if (course) this.chooseCourse(course);
    },

    chooseCourse(course) {
      const changed = String(course.id) !== String(this.courseId);
      this.courseId = String(course.id);
      this.courseQuery = course.title;
      this.courseListOpen = false;
      this.activeCourseIndex = -1;
      if (changed) this.onCourseChange();
    },

    async loadCourses() {
      this.loadingCourses = true;
      this.coursesError = "";
      try {
        const resp = await fetch(this.coursesUrl, { headers: { Accept: "application/json" } });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          this.coursesError = data.error || `Failed to load courses (${resp.status})`;
          return;
        }
        this.courses = await resp.json();
        if (this.selectedCourseTitle) this.courseQuery = this.selectedCourseTitle;
      } catch (e) {
        this.coursesError = e.message;
      } finally {
        this.loadingCourses = false;
      }
    },

    onCourseChange() {
      this.courseSettingId = "";
      this.settings = [];
      if (this.mode === "course_access" && this.courseId) {
        this.loadSettings();
      }
    },

    async loadSettings() {
      this.loadingSettings = true;
      this.settingsError = "";
      try {
        const url = `${this.settingsUrl}?course_id=${encodeURIComponent(this.courseId)}`;
        const resp = await fetch(url, { headers: { Accept: "application/json" } });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          this.settingsError = data.error || `Failed to load settings (${resp.status})`;
          return;
        }
        this.settings = await resp.json();
      } catch (e) {
        this.settingsError = e.message;
      } finally {
        this.loadingSettings = false;
      }
    }
  }));
}
